import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Loader2,
  Monitor,
  Radio,
  Smartphone,
  Tablet,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

const REFRESH_MS = 5000;

function stamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
      });
}

function duration(seconds, arrivedLabel = "just arrived") {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 2) return arrivedLabel;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const DeviceIcon = ({ device }) => {
  const Icon = device === "Phone" ? Smartphone : device === "Tablet" ? Tablet : Monitor;
  return <Icon className="h-4 w-4 text-slate-500" />;
};

const Stat = ({ label, value, hint, accent }) => (
  <Card className={`border-slate-200 ${accent ? "border-emerald-300 bg-emerald-50" : ""}`}>
    <CardContent className="p-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1.5 font-display text-3xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </CardContent>
  </Card>
);

const TopList = ({ title, rows, testId }) => (
  <Card className="border-slate-200">
    <CardContent className="p-5">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <ul className="mt-3 space-y-2" data-testid={testId}>
        {(rows || []).length === 0 && <li className="text-xs text-slate-500">No data yet.</li>}
        {(rows || []).map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-slate-700">{r.label}</span>
            <span className="shrink-0 font-semibold text-slate-900">{r.count}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

function VisitorRow({ visitor, expanded, onToggle }) {
  const isStaff = visitor.role === "admin" || visitor.role === "partner";
  return (
    <>
      <tr
        className="cursor-pointer border-t border-slate-100 align-top hover:bg-slate-50"
        onClick={onToggle}
        data-testid={`visitor-row-${visitor.sid}`}
      >
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )}
            <span
              className={`inline-flex h-2 w-2 rounded-full ${
                visitor.status === "Active" ? "bg-emerald-500" : "bg-amber-400"
              }`}
            />
            <span className="text-xs text-slate-600">{visitor.status}</span>
          </div>
        </td>
        <td className="px-3 py-3 font-mono text-xs text-slate-800">{visitor.ip}</td>
        <td className="px-3 py-3 text-sm text-slate-700">{visitor.location}</td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <DeviceIcon device={visitor.device} />
            {visitor.device}
          </div>
        </td>
        <td className="px-3 py-3 text-sm text-slate-700">
          {visitor.browser} {visitor.browser_version}
          <div className="text-xs text-slate-500">
            {visitor.os} {visitor.os_version}
          </div>
        </td>
        <td className="px-3 py-3 text-sm text-slate-700">{visitor.source}</td>
        <td className="px-3 py-3 text-sm text-slate-700">
          {visitor.current_page}
          <div className="text-xs text-slate-500">{visitor.pageviews} page(s)</div>
        </td>
        <td className="px-3 py-3 text-sm font-semibold text-slate-900">
          {duration(visitor.seconds_on_site)}
        </td>
        <td className="px-3 py-3">
          {visitor.bot ? (
            <Badge className="bg-slate-700 text-white" data-testid="visitor-bot-badge">
              BOT
            </Badge>
          ) : isStaff ? (
            <Badge className="bg-blue-600 text-white" data-testid="visitor-staff-badge">
              {visitor.role === "admin" ? "ADMIN" : "PARTNER"}
            </Badge>
          ) : (
            <span className="text-xs text-slate-400">Visitor</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-slate-100 bg-slate-50/70">
          <td colSpan={9} className="px-6 py-4">
            <div className="grid gap-5 lg:grid-cols-2">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Page journey
                </h4>
                <ol
                  className="mt-2 space-y-1.5 border-l border-slate-200 pl-4"
                  data-testid={`visitor-journey-${visitor.sid}`}
                >
                  {visitor.page_history.map((p, i) => (
                    <li key={`${p.at}-${i}`} className="text-sm text-slate-700">
                      <span className="font-medium">{p.path}</span>
                      <span className="ml-2 text-xs text-slate-400">{stamp(p.at)}</span>
                    </li>
                  ))}
                </ol>
                {visitor.events.length > 0 && (
                  <>
                    <h4 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Enrollment activity
                    </h4>
                    <ul className="mt-2 space-y-1" data-testid={`visitor-events-${visitor.sid}`}>
                      {visitor.events.map((e, i) => (
                        <li key={`${e.at}-${i}`} className="text-sm text-slate-700">
                          {e.name}
                          <span className="ml-2 text-xs text-slate-400">{stamp(e.at)}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {[
                  ["Landing page", visitor.landing_page],
                  ["Referrer", visitor.referrer || "Direct"],
                  ["Campaign", visitor.utm?.campaign || "—"],
                  ["ISP", visitor.isp || "—"],
                  ["Network", visitor.network || visitor.org || "—"],
                  ["Screen", visitor.screen || "—"],
                  ["Window", visitor.viewport || "—"],
                  ["Language", visitor.language || "—"],
                  ["Their timezone", visitor.timezone || "—"],
                  ["Signed in as", visitor.user_email || "Not signed in"],
                  ["First seen", stamp(visitor.started_at)],
                  ["Last activity", stamp(visitor.last_seen)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
                    <dd className="truncate text-slate-800">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const HEADERS = ["Status", "IP address", "Location", "Device", "Browser / OS", "Came from",
  "Current page", "Time on site", ""];

function VisitorTable({ rows, expanded, toggle, emptyText, testId }) {
  const unique = Array.from(new Map((rows || []).map((r) => [r.sid, r])).values());
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] text-left" data-testid={testId}>
        <thead>
          <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {HEADERS.map((h, i) => (
              <th key={`${h}-${i}`} className="px-3 pb-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {unique.length === 0 && (
            <tr>
              <td colSpan={9} className="px-3 py-6 text-sm text-slate-500">
                {emptyText}
              </td>
            </tr>
          )}
          {unique.map((v) => (
            <VisitorRow
              key={v.sid}
              visitor={v}
              expanded={!!expanded[v.sid]}
              onToggle={() => toggle(v.sid)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminLiveUsers() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showBots, setShowBots] = useState(false);
  const [pulse, setPulse] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/live-users");
      if (!mounted.current) return;
      setData(res.data);
      setPulse(true);
      setTimeout(() => mounted.current && setPulse(false), 600);
    } catch {
      /* keep the last snapshot on a hiccup */
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load]);

  const toggle = (sid) => setExpanded((prev) => ({ ...prev, [sid]: !prev[sid] }));

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Live Visitors & Web Stats"
      subtitle="Auto-refreshing every 5 seconds — visible only to ACS admins"
    >
      {!data ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      ) : (
        <div className="space-y-8">
          <div className="flex flex-wrap items-center gap-3">
            <Radio
              className={`h-4 w-4 text-emerald-600 transition-opacity ${
                pulse ? "opacity-100" : "opacity-40"
              }`}
            />
            <span className="text-sm text-slate-600" data-testid="live-updated-at">
              Updated {stamp(data.generated_at)}
            </span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Live right now"
              value={data.live_count}
              hint="Excludes you"
              accent
            />
            <Stat
              label="Visitors today"
              value={data.today.visitors}
              hint={`${data.today.pageviews} pageviews · avg ${duration(data.today.avg_seconds, "0s")}`}
            />
            <Stat
              label="Visitors — 7 days"
              value={data.week.visitors}
              hint={`${data.week.pageviews} pageviews`}
            />
            <Stat
              label="Visitors — 30 days"
              value={data.month.visitors}
              hint={`${data.month.pageviews} pageviews · avg ${duration(data.month.avg_seconds, "0s")}`}
            />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h2 className="text-base font-semibold text-slate-900">
                On the site now ({data.live.length})
              </h2>
              <p className="mb-3 mt-1 text-xs text-slate-500">
                One row per person — click a row for their full journey and details.
              </p>
              <VisitorTable
                rows={data.live}
                expanded={expanded}
                toggle={toggle}
                testId="live-visitors-table"
                emptyText="Nobody is on the site right now."
              />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <TopList title="Top pages (30 days)" rows={data.top_pages} testId="top-pages" />
            <TopList title="Came from (30 days)" rows={data.top_sources} testId="top-sources" />
            <TopList title="Devices (30 days)" rows={data.devices} testId="top-devices" />
            <TopList title="Countries (30 days)" rows={data.countries} testId="top-countries" />
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <button
                type="button"
                onClick={() => setShowBots((v) => !v)}
                className="flex w-full items-center gap-3 text-left"
                data-testid="toggle-bots-section"
              >
                {showBots ? (
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                )}
                <Bot className="h-4 w-4 text-slate-400" />
                <span className="text-base font-semibold text-slate-900">
                  Bots &amp; scanners
                </span>
                <span className="text-xs text-slate-500" data-testid="bots-blocked-count">
                  {data.bots_blocked_24h} filtered out in the last 24 hours
                </span>
              </button>
              {showBots && (
                <>
                  <p className="mb-3 mt-2 text-xs text-slate-500">
                    Traffic from cloud and hosting networks (AWS, Google Cloud, Azure and the
                    like). Crawlers and scanners, not families — kept out of your stats and
                    never emailed to you.
                  </p>
                  <VisitorTable
                    rows={data.bots}
                    expanded={expanded}
                    toggle={toggle}
                    testId="bots-table"
                    emptyText="No bot traffic in the last 24 hours."
                  />
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-5">
              <h2 className="text-base font-semibold text-slate-900">Recently left</h2>
              <p className="mb-3 mt-1 text-xs text-slate-500">
                The last 50 sessions that have gone idle.
              </p>
              <VisitorTable
                rows={data.recent}
                expanded={expanded}
                toggle={toggle}
                testId="recent-visitors-table"
                emptyText="No past sessions yet."
              />
            </CardContent>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
